import { useEffect } from 'react';
import { useRouteError, isRouteErrorResponse } from 'react-router-dom';

import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Button from '@mui/material/Button';
import Container from '@mui/material/Container';
import Typography from '@mui/material/Typography';

import { reportClientError } from 'src/lib/error-reporter';

// ----------------------------------------------------------------------

/**
 * Route-level error boundary. Replaces React Router's default dev error page
 * with a friendly fallback AND reports the render crash to the make.com webhook
 * (the backend/API error path stays handled by BackendErrorMonitor).
 *
 * Rendered as a pathless route INSIDE RootLayout, so theme/i18n providers are
 * still mounted around this UI.
 */
export function RouteErrorBoundary() {
  const error = useRouteError();

  const message = isRouteErrorResponse(error)
    ? `${error.status} ${error.statusText}`
    : error instanceof Error
      ? error.message
      : 'An unexpected error occurred';

  useEffect(() => {
    // Don't report 404s and other routing responses - only real crashes.
    if (!isRouteErrorResponse(error)) {
      reportClientError(error, { source: 'route-error-boundary' });
    }
  }, [error]);

  return (
    <Container component="main" sx={{ py: 10 }}>
      <Stack spacing={3} sx={{ alignItems: 'center', textAlign: 'center' }}>
        <Typography variant="h3">Something went wrong</Typography>

        <Typography sx={{ color: 'text.secondary' }}>
          The app hit an unexpected error. Our team has been notified. Please try again.
        </Typography>

        {import.meta.env.DEV && (
          <Box
            component="pre"
            sx={{
              p: 2,
              width: 1,
              maxWidth: 720,
              overflow: 'auto',
              textAlign: 'left',
              borderRadius: 1,
              fontSize: 13,
              bgcolor: 'background.neutral',
            }}
          >
            {message}
            {error instanceof Error && error.stack ? `\n\n${error.stack}` : null}
          </Box>
        )}

        <Button variant="contained" size="large" onClick={() => window.location.reload()}>
          Reload app
        </Button>
      </Stack>
    </Container>
  );
}
